import React from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, LayoutGrid } from 'lucide-react';
import PageHeader from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import {
  bsiInternetBankApi,
  type BsiIbConnection,
  type BsiIbStatus,
} from '@/lib/banks/bsi/api';
import BsiAccountsTab from './BsiAccountsTab';
import BsiBatchLocalTab from './BsiBatchLocalTab';
import BsiBatchPayaTab from './BsiBatchPayaTab';
import BsiChequeTab from './BsiChequeTab';
import BsiDestinationsTab from './BsiDestinationsTab';
import BsiIbanInquiryTab from './BsiIbanInquiryTab';
import BsiInterbankTab from './BsiInterbankTab';
import BsiLastThirtyTab from './BsiLastThirtyTab';
import BsiLoginTab from './BsiLoginTab';
import BsiMetroHome from './BsiMetroHome';
import BsiPendingTab from './BsiPendingTab';
import BsiPeriodTab from './BsiPeriodTab';
import BsiReceiptTab from './BsiReceiptTab';
import BsiSessionStatusCard from './BsiSessionStatusCard';
import BsiProfileSwitcher from './BsiProfileSwitcher';
import BsiTransferTab from './BsiTransferTab';
import {
  BSI_SERVICE_TILES,
  bsiServicePath,
  isBsiTabKey,
  parseBsiTabSlug,
  type BsiFlashMessage,
  type BsiTabKey,
} from './helpers';

function normalizeLegacyTab(raw: string | null): BsiTabKey | null {
  if (!raw) return null;
  if (raw === 'batch-paya' || raw === 'batch_paya') return 'batch_paya';
  if (raw === 'batch-local' || raw === 'batch_local') return 'batch_local';
  if (isBsiTabKey(raw)) return raw;
  return parseBsiTabSlug(raw);
}

const BsiInternetBankPage: React.FC = () => {
  const navigate = useNavigate();
  const { section } = useParams<{ section?: string }>();
  const [searchParams] = useSearchParams();

  const parsedTab = parseBsiTabSlug(section);
  const tab: BsiTabKey = parsedTab ?? 'home';
  const sectionValid = section == null || section === '' || parsedTab != null;
  const legacyTab = normalizeLegacyTab(searchParams.get('tab'));

  const [status, setStatus] = React.useState<BsiIbStatus | null>(null);
  const [connection, setConnection] = React.useState<BsiIbConnection | null>(null);
  const [loadingStatus, setLoadingStatus] = React.useState(true);
  const [checking, setChecking] = React.useState(false);
  const [message, setMessage] = React.useState<BsiFlashMessage | null>(null);
  const [preferredAccount, setPreferredAccount] = React.useState('');
  const [tabResetKey, setTabResetKey] = React.useState(0);
  const [statusReady, setStatusReady] = React.useState(false);
  const lastActivityRef = React.useRef(Date.now());

  const markUserActivity = React.useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const goTo = React.useCallback(
    (next: BsiTabKey, opts?: { replace?: boolean }) => {
      navigate(bsiServicePath(next), { replace: opts?.replace });
    },
    [navigate],
  );

  const loadStatus = React.useCallback(async () => {
    setLoadingStatus(true);
    try {
      const res = await bsiInternetBankApi.status();
      setStatus(res.data);
      if (res.data.session_valid) {
        markUserActivity();
        // فقط وقتی وضعیت تازه خوانده می‌شود و هنوز روی صفحه ورود هستیم
        if (parseBsiTabSlug(section) === 'login') {
          navigate(bsiServicePath('home'), { replace: true });
        }
      }
    } catch (e) {
      setMessage({ type: 'error', text: e instanceof Error ? e.message : 'خطا در خواندن وضعیت' });
    } finally {
      setLoadingStatus(false);
      setStatusReady(true);
    }
  }, [markUserActivity, section, navigate]);

  React.useEffect(() => {
    void loadStatus();
    // فقط یک‌بار در mount — نه با هر تغییر section
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sessionValid = Boolean(status?.session_valid);
  const idleTimeoutMinutes = status?.idle_timeout_minutes || 7;
  const activeTile = BSI_SERVICE_TILES.find((t) => t.key === tab);
  const inService = tab !== 'home';

  React.useEffect(() => {
    if (!sessionValid || legacyTab || !sectionValid) return;

    const idleLimitMs = idleTimeoutMinutes * 60 * 1000;
    const tickMs = 2.5 * 60 * 1000;

    const onActivity = () => markUserActivity();
    window.addEventListener('pointerdown', onActivity);
    window.addEventListener('keydown', onActivity);
    window.addEventListener('focus', onActivity);

    void (async () => {
      try {
        const res = await bsiInternetBankApi.keepalive();
        setStatus(res.data.status);
        if (!res.data.session_valid) {
          goTo('login');
          setMessage({ type: 'error', text: res.message || 'سشن بانک منقضی شده است.' });
        }
      } catch {
        await loadStatus();
      }
    })();

    const timer = window.setInterval(() => {
      void (async () => {
        const idleMs = Date.now() - lastActivityRef.current;
        if (idleMs >= idleLimitMs) {
          await loadStatus();
          goTo('login');
          setMessage({
            type: 'error',
            text: `سشن به‌خاطر ${idleTimeoutMinutes} دقیقه عدم‌فعالیت منقضی شد.`,
          });
          return;
        }
        try {
          const res = await bsiInternetBankApi.keepalive();
          setStatus(res.data.status);
          if (!res.data.session_valid) {
            goTo('login');
            setMessage({ type: 'error', text: res.message || 'سشن بانک منقضی شده است.' });
          }
        } catch (e) {
          setMessage({
            type: 'error',
            text: e instanceof Error ? e.message : 'تمدید سشن بانک ناموفق بود.',
          });
          await loadStatus();
        }
      })();
    }, tickMs);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener('pointerdown', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('focus', onActivity);
    };
  }, [sessionValid, idleTimeoutMinutes, markUserActivity, loadStatus, goTo, legacyTab, sectionValid]);

  if (legacyTab) {
    return <Navigate to={bsiServicePath(legacyTab)} replace />;
  }

  if (!sectionValid) {
    return <Navigate to={bsiServicePath('home')} replace />;
  }

  const handleCheckConnection = async () => {
    markUserActivity();
    setChecking(true);
    setMessage(null);
    try {
      const res = await bsiInternetBankApi.connection();
      setConnection(res.data);
      setMessage({ type: res.data.ok ? 'success' : 'error', text: res.data.message });
      await loadStatus();
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'بررسی اتصال ناموفق بود.' });
    } finally {
      setChecking(false);
    }
  };

  const handleLogout = async () => {
    try {
      const res = await bsiInternetBankApi.logout();
      setStatus(res.data);
      setConnection(null);
      setPreferredAccount('');
      setTabResetKey((k) => k + 1);
      goTo('home');
      setMessage({ type: 'success', text: res.message || 'سشن پاک شد.' });
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'خروج ناموفق بود.' });
    }
  };

  const openService = (key: BsiTabKey) => {
    markUserActivity();
    goTo(key);
  };

  const showLoginForm = statusReady && !sessionValid && tab === 'login';

  return (
    <div className="w-full min-w-0 space-y-6">
      <PageHeader
        title={status?.profile ? `بانک صادرات · ${status.profile.name}` : 'بانک صادرات'}
        description={
          status?.profile
            ? `${status.profile.portal_label} — ${status.profile.base_url}`
            : 'خدمات اینترنت‌بانک؛ برای حقوقی و حقیقی پروفایل جدا بسازید.'
        }
        actions={
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline">
              <Link to="/banks">
                <ArrowLeft className="ml-1 h-4 w-4" />
                لیست بانک‌ها
              </Link>
            </Button>
            <Button asChild variant="outline">
              <a
                href={status?.profile?.base_url ? `${status.profile.base_url}/public-page.ib` : 'https://bib.bsi.ir/public-page.ib'}
                target="_blank"
                rel="noreferrer"
              >
                <ExternalLink className="ml-1 h-4 w-4" />
                سایت بانک
              </a>
            </Button>
          </div>
        }
      />

      {message && (
        <div
          className={`rounded-lg border px-4 py-3 text-sm ${
            message.type === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-rose-200 bg-rose-50 text-rose-800'
          }`}
        >
          {message.text}
        </div>
      )}

      <BsiProfileSwitcher
        status={status}
        onStatusChange={(next) => {
          setStatus(next);
          setTabResetKey((k) => k + 1);
          setPreferredAccount('');
          setConnection(null);
        }}
        onMessage={setMessage}
        onProfileSwitched={() => {
          markUserActivity();
          goTo('home', { replace: true });
        }}
      />

      {inService && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
          <div>
            <div className="text-sm font-semibold text-slate-900">{activeTile?.title || 'خدمت'}</div>
            <p className="text-xs text-slate-500">{activeTile?.description}</p>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              markUserActivity();
              goTo('home');
            }}
          >
            <LayoutGrid className="ml-1 h-4 w-4" />
            بازگشت به خدمات
          </Button>
        </div>
      )}

      <BsiSessionStatusCard
        status={status}
        connection={connection}
        loadingStatus={loadingStatus}
        checking={checking}
        onRefreshStatus={() => {
          markUserActivity();
          void loadStatus();
        }}
        onCheckConnection={() => void handleCheckConnection()}
        onLogout={() => void handleLogout()}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {tab === 'home' && (
          <BsiMetroHome
            sessionValid={sessionValid}
            onOpen={(key) => {
              if (key !== 'login' && !sessionValid) {
                setMessage({ type: 'error', text: 'ابتدا از باکس ورود، به اینترنت‌بانک متصل شوید.' });
                goTo('login');
                return;
              }
              openService(key);
            }}
          />
        )}

        {tab === 'login' && showLoginForm && (
          <BsiLoginTab
            key={`login-${tabResetKey}`}
            status={status}
            onStatusChange={setStatus}
            onMessage={setMessage}
            onLoggedIn={() => {
              markUserActivity();
              goTo('home');
            }}
          />
        )}
        {tab === 'login' && sessionValid && (
          <div className="lg:col-span-3 space-y-3">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              سشن اینترنت‌بانک فعال است. برای ورود مجدد ابتدا «خروج سشن» بزنید.
            </div>
            <Button type="button" variant="outline" onClick={() => goTo('home')}>
              بازگشت به خدمات
            </Button>
          </div>
        )}
        {tab === 'accounts' && (
          <BsiAccountsTab
            key={`accounts-${tabResetKey}`}
            sessionValid={sessionValid}
            onMessage={setMessage}
            onSessionExpired={() => void loadStatus()}
            onOpenLastThirty={(acc) => {
              setPreferredAccount(acc);
              goTo('last30');
            }}
            onOpenPeriod={(acc) => {
              setPreferredAccount(acc);
              goTo('period');
            }}
          />
        )}
        {tab === 'last30' && (
          <BsiLastThirtyTab
            key={`last30-${tabResetKey}`}
            sessionValid={sessionValid}
            preferredAccount={preferredAccount}
            onMessage={setMessage}
            onSessionExpired={() => void loadStatus()}
          />
        )}
        {tab === 'period' && (
          <BsiPeriodTab
            key={`period-${tabResetKey}`}
            sessionValid={sessionValid}
            preferredAccount={preferredAccount}
            onMessage={setMessage}
            onSessionExpired={() => void loadStatus()}
          />
        )}
        {tab === 'destinations' && (
          <BsiDestinationsTab
            key={`dest-${tabResetKey}`}
            sessionValid={sessionValid}
            onMessage={setMessage}
            onSessionExpired={() => void loadStatus()}
          />
        )}
        {tab === 'transfer' && (
          <BsiTransferTab
            key={`xfer-${tabResetKey}`}
            sessionValid={sessionValid}
            onMessage={setMessage}
            onSessionExpired={() => void loadStatus()}
          />
        )}
        {tab === 'interbank' && (
          <BsiInterbankTab
            key={`interbank-${tabResetKey}`}
            sessionValid={sessionValid}
            onMessage={setMessage}
            onSessionExpired={() => void loadStatus()}
          />
        )}
        {tab === 'batch_local' && (
          <BsiBatchLocalTab
            key={`batch-local-${tabResetKey}`}
            sessionValid={sessionValid}
            onMessage={setMessage}
            onSessionExpired={() => void loadStatus()}
          />
        )}
        {tab === 'batch_paya' && (
          <BsiBatchPayaTab
            key={`batch-${tabResetKey}`}
            sessionValid={sessionValid}
            onMessage={setMessage}
            onSessionExpired={() => void loadStatus()}
          />
        )}
        {tab === 'pending' && (
          <BsiPendingTab
            key={`pending-${tabResetKey}`}
            sessionValid={sessionValid}
            onMessage={setMessage}
            onSessionExpired={() => void loadStatus()}
          />
        )}
        {tab === 'iban' && (
          <BsiIbanInquiryTab
            key={`iban-${tabResetKey}`}
            sessionValid={sessionValid}
            onMessage={setMessage}
            onSessionExpired={() => void loadStatus()}
          />
        )}
        {tab === 'receipt' && (
          <BsiReceiptTab
            key={`receipt-${tabResetKey}`}
            sessionValid={sessionValid}
            onMessage={setMessage}
            onSessionExpired={() => void loadStatus()}
          />
        )}
        {tab === 'cheque' && (
          <BsiChequeTab
            key={`cheque-${tabResetKey}`}
            sessionValid={sessionValid}
            onMessage={setMessage}
            onSessionExpired={() => void loadStatus()}
          />
        )}
      </div>
    </div>
  );
};

export default BsiInternetBankPage;
